import { useEffect, useRef } from 'react';

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousedown',
  'keydown',
  'touchstart',
];

export function useIdleLock(
  enabled: boolean,
  onLock: () => void,
  timeoutMinutes = 10,
): void {
  const lastActivity = useRef(0);
  const lockCallback = useRef(onLock);

  useEffect(() => {
    lockCallback.current = onLock;
  }, [onLock]);

  useEffect(() => {
    if (!enabled) return;

    let timeoutId: number;
    const scheduleLock = () => {
      window.clearTimeout(timeoutId);
      const remaining = Math.max(
        0,
        timeoutMinutes * 60 * 1000 - (Date.now() - lastActivity.current),
      );
      timeoutId = window.setTimeout(() => lockCallback.current(), remaining);
    };
    const recordActivity = () => {
      lastActivity.current = Date.now();
      scheduleLock();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') scheduleLock();
    };

    lastActivity.current = Date.now();
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, recordActivity, { passive: true }),
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);
    scheduleLock();

    return () => {
      window.clearTimeout(timeoutId);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, recordActivity),
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, timeoutMinutes]);
}
